import { DatePipe, NgClass, NgStyle } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Guid } from 'guid-typescript';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  AssemblyOrderModel,
  AssemblyOrderScheduleLogModel,
} from 'src/app/models/assembly-order.model';
import { AssemblyTaskModel } from 'src/app/models/assembly-task.model';
import { PoolShell } from 'src/app/models/pool-shell.model';
import { AssemblyOrderService } from 'src/app/services/assembly-order.service';
import { ManufacturingOrderStatusService } from 'src/app/services/manufacturing-order-status.service';
import { ToastService } from 'src/app/services/toast.service';
import { AssemblyOrderEnum } from 'src/app/shared/enumerators/assembly-order.enum';
import { PoolShellStatusEnum } from 'src/app/shared/enumerators/pool-shell-status.enum';
import { ProductStatusEnum } from 'src/app/shared/enumerators/product-status.enum';
import { UserService } from 'src/app/shared/services/user.service';
import { SAP_PoolStates } from 'src/assets/data/sap_product_states';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatDivider } from '@angular/material/divider';
import { MatDialog } from '@angular/material/dialog';
import { StateSwitcherComponent } from '../../shared/components/state-switcher/state-switcher.component';
import { InTransitComponent } from '../../shared/components/in-transit/in-transit.component';
import { FormsModule } from '@angular/forms';
import { CommonFunctionsConfiguratorService } from 'src/app/shared/services/swal-fire-configurator.service';
import { AssemblyTaskModalComponent } from './assembly-task-modal/assembly-task-modal.component';
import { IconMenuButtonComponent } from 'src/app/shared/components/icon-menu-button/icon-menu-button.component';
import { HideIfNoPermissionDirective } from 'src/app/directives/HideIfNoPermissionDirective';

@Component({
    selector: 'manufacturing-order-details',
    templateUrl: './manufacturing-order-details.component.html',
    styleUrls: ['./manufacturing-order-details.component.scss'],
    imports: [NgClass, MatIcon, MatMenu, MatMenuItem, MatDivider, NgStyle, StateSwitcherComponent, InTransitComponent, FormsModule, DatePipe, IconMenuButtonComponent, HideIfNoPermissionDirective]
})
export class ManufacturingOrderDetailsComponent implements OnInit {
  private readonly assemblySvc = inject(AssemblyOrderService);
  private readonly moStatusService = inject(ManufacturingOrderStatusService);
  private readonly swalFire = inject(CommonFunctionsConfiguratorService);
  private readonly dialog = inject(MatDialog);
  usrSvc = inject(UserService);
  datepipe = inject(DatePipe);
  private readonly toastr = inject(ToastService);
  activatedRoute = inject(ActivatedRoute);

  @Input() assemblyOrder!: AssemblyOrderModel;
  @Output() statusChangedResult = new EventEmitter<AssemblyOrderModel>();

  @ViewChild('contentToPrint', { static: false }) contentToPrint!: ElementRef;
  disablePrint: boolean = false;

  poolColor: string = '';

  poolStates = SAP_PoolStates;
  expandedIndex: number = -1;
  scheduleTaskExpanded: boolean = false;
  scheduleTaskLogs: AssemblyOrderScheduleLogModel[] = [];
  _assemblyOrderEnum = AssemblyOrderEnum;
  _productStatusEnum = ProductStatusEnum;
  observations: string = '';
  obsEdit: boolean = false;

  ngOnInit(): void {
    this.assemblyOrder.poolShell.expanded = false;
  }

  getOrderStatus(status: AssemblyOrderEnum) {
    switch (status) {
      case AssemblyOrderEnum.Bloqueada:
        return 'OABloqueada';
      case AssemblyOrderEnum.EnPreparacion:
        return 'OAEnPreparacion';
      case AssemblyOrderEnum.Armada:
        return 'OAArmada';
      case AssemblyOrderEnum.Modificada:
        return 'OAModificada';
      case AssemblyOrderEnum.Cancelada:
        return 'OACancelada';
      case AssemblyOrderEnum.Reprogramada:
        return 'OAReprogramada';
      case AssemblyOrderEnum.Programado:
        return 'OAProgramada';
      case AssemblyOrderEnum.Pospuesta:
        return 'OAPospuesta';
      case AssemblyOrderEnum.Pendiente:
      default:
        return 'OAPendiente';
    }
  }

  async enableDisable(paid: boolean, id: string) {
    let date = this.datepipe.transform(
      new Date(),
      'yyyy-MM-ddTHH:mm:ss' + this.getOffSet()
    );
    if (date) {
      this.inTransitChange(false);
      const success = await this.assemblySvc.enableDisableOA(id, paid, date);
      if (success) {
        this.assemblyOrder.paid = paid;
      }
      return success;
    } else {
      this.toastr.error(
        'Error',
        'Error en formato de la fecha para actualización de tarea.'
      );
      return false;
    }
  }

  getOffSet(): string {
    let offSet = new Date().getTimezoneOffset() / -60;
    let offSetFormated = Math.abs(offSet).toString().padStart(2, '0');
    return (offSet >= 0 ? '+' : '-') + offSetFormated + ':00';
  }

  getPoolShellDisabledState() {
    return (
      this.assemblyOrder.statusId == AssemblyOrderEnum.Cancelada || //disable button if task is Cancelled, (bloqued could be allowed).
      !this.assemblyOrder.poolShell.permissions ||
      !this.usrSvc.hasPermission(this.assemblyOrder.poolShell.permissions)
    );
  }

  getAssemblyTaskDisabledState(permissions?: string) {
    return (
      [AssemblyOrderEnum.Cancelada, AssemblyOrderEnum.Bloqueada].includes(
        this.assemblyOrder.statusId
      ) || // disable button if task is cancelled or bloqued
      !permissions ||
      !this.usrSvc.hasPermission(permissions)
    );
  }

  getProdStatus(statusId: ProductStatusEnum): string {
    return this.moStatusService.getProdStatus(statusId);
  }

  getPoolStatus(status: PoolShellStatusEnum): string {
    return this.moStatusService.getPoolStatus(status);
  }

  get isOrderFinished(): boolean {
    return (
      this.assemblyOrder.poolShell.statusId == PoolShellStatusEnum.Lista &&
      this.assemblyOrder.assemblyTasks.every(
        (x) => x.statusId == ProductStatusEnum.Lista
      )
    );
  }

  onChangedOrderState() {
    this.statusChangedResult.emit(this.assemblyOrder);
    if (this.assemblyOrder.poolShell.statusId != PoolShellStatusEnum.Lista) {
      this.inTransitChange(false);
    }
  }

  async itemStatusChanger(itemId: Guid, statusId: number) {
    return await this.assemblySvc.updateItemStatus(itemId, statusId);
  }

  async poolShellStatusChanger(itemId: Guid, statusId: any) {
    return await this.assemblySvc.updatePoolShellStatus(itemId, statusId);
  }

  getLogs(task: AssemblyTaskModel) {
    this.assemblySvc
      .getAssemblyTaskLogs(this.assemblyOrder.id, task.itemCategoryId)
      .subscribe((res) => {
        task.logs = res;
      });
  }

  getPoolShellLogs(poolShell: PoolShell) {
    this.assemblySvc
      .getAssemblyTaskLogs(this.assemblyOrder.id, poolShell.itemCategoryId)
      .subscribe((res) => {
        poolShell.logs = res;
      });
  }

  getScheduleTaskLogs() {
    this.assemblySvc.getScheduleTaskLogs(this.assemblyOrder.id).subscribe({
      next: (data) => {
        this.scheduleTaskLogs = data;
      },
      error: (err) => {
        console.error(
          'Error al obtener los logs de la agenda de esta orden de montaje: ',
          err
        );
      },
    });
  }

  toggleTaskExpansion(index: number) {
    if (this.expandedIndex === index) {
      this.expandedIndex = -1; // Contraer el elemento actualmente expandido
    } else {
      this.expandedIndex = index; // Expandir el nuevo elemento
    }
  }

  togglePoolshellExpansion() {
    this.assemblyOrder.poolShell.expanded =
      !this.assemblyOrder.poolShell.expanded;
  }

  toggleScheduleTaskExpansion() {
    this.scheduleTaskExpanded = !this.scheduleTaskExpanded;
  }

  disableClick() {
    this.disablePrint = true;
    setTimeout(() => {
      this.disablePrint = false;
    }, 10000);
  }

  async inTransitChange(disable: boolean) {
    if (
      this.assemblyOrder.poolShell.statusId != PoolShellStatusEnum.Lista ||
      this.assemblyOrder.poolShell.inTransit
    ) {
      this.assemblySvc.updatePoolShellTransit(
        this.assemblyOrder.poolShell.id,
        false
      );
    }
  }

  observationsEnable() {
    this.obsEdit = true;
    this.observations = this.assemblyOrder.observations;
  }

  updateObservations() {
    this.obsEdit = false;
    this.assemblySvc
      .updateObservations(this.assemblyOrder.id, this.observations)
      .subscribe({
        next: () => {
          this.toastr.success(
            'Observaciones actualizadas',
            'Las observaciones se actualizaron correctamente para la OA.'
          );
        },
        error: (err) => {
          this.toastr.error(
            'Erro al actualizar observaciones',
            'No se pudo actualizar las observaciones para la OA.' + err.error
          );
        },
      });
  }

  printPDF() {
    this.disableClick();
    this.toastr.warning(
      'Generando impresión',
      'Generando el archivo de impresión, por favor no actualice el navegador.'
    );

    // Obtener el bloque que se quiere imprimir
    const DATA: any = this.contentToPrint.nativeElement;

    // Aplicar tamaño de fuente a cada elemento hijo
    const allElements = DATA.querySelectorAll('*');
    allElements.forEach((element: HTMLElement) => {
      element.style.fontSize = '25px';
    });

    const doc = new jsPDF('p', 'pt', [6 * 28.35, 10 * 28.35]);
    const options = {
      background: 'white',
      scale: 3,
    };

    // Generar la imagen a imprimir
    html2canvas(DATA, options).then((canvas) => {
      // Revertir el estilo temporal después de capturar la imagen
      allElements.forEach((element: HTMLElement) => {
        element.style.fontSize = ''; // Revertir el tamaño de fuente a su valor original
      });

      const img = canvas.toDataURL('image/PNG');

      const bufferX = 0;
      const bufferY = 0;
      const imgProps = (doc as any).getImageProperties(img);
      const pdfWidth = doc.internal.pageSize.getWidth() - 2 * bufferX;
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      doc.addImage(
        img,
        'PNG',
        bufferX,
        bufferY,
        pdfWidth,
        pdfHeight,
        undefined,
        'FAST'
      );

      // Abrir documento a imprimir en una pestaña
      window.open(doc.output('bloburl'), '_blank');
    });
  }

  @HostListener('document:keydown', ['$event'])
  captureCtrlP(event: KeyboardEvent) {
    if (event.ctrlKey && event.key === 'p') {
      event.preventDefault();
      this.printPDF();
    }
  }

  openAddTaskModal() {
    this.openTaskModal(undefined);
  }

  openEditTaskModal(task: AssemblyTaskModel) {
    this.openTaskModal(task);
  }

  private openTaskModal(task: AssemblyTaskModel | undefined) {
    const existingInternalCodes = this.assemblyOrder.assemblyTasks
      .filter((x) => x.id.toString() !== task?.id?.toString())
      .map((x) => x.internalCode);

    const dialogRef = this.dialog.open(AssemblyTaskModalComponent, {
      data: {
        assemblyOrderId: this.assemblyOrder.id.toString(),
        task,
        existingInternalCodes,
      },
      width: '560px',
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((result: AssemblyTaskModel | undefined) => {
      if (result) {
        this.onTaskSaved(result);
      }
    });
  }

  onTaskSaved(task: AssemblyTaskModel) {
    const index = this.assemblyOrder.assemblyTasks.findIndex(
      (x) => x.id.toString() == task.id.toString()
    );
    if (index > -1) {
      this.assemblyOrder.assemblyTasks[index] = task;
    } else {
      this.assemblyOrder.assemblyTasks.push(task);
    }
    this.statusChangedResult.emit(this.assemblyOrder);
  }

  deleteTask(task: AssemblyTaskModel) {
    this.swalFire
      .SwalFireQuestion(
        'Eliminar ítem',
        '¿Está seguro que desea eliminar el ítem de la orden de montaje?',
        'Eliminar',
        'Cancelar'
      )
      .then((result) => {
        if (result.isConfirmed) {
          this.assemblySvc.deleteAssemblyTask(task.id.toString()).subscribe({
            next: () => {
              this.assemblyOrder.assemblyTasks =
                this.assemblyOrder.assemblyTasks.filter(
                  (x) => x.id.toString() != task.id.toString()
                );
              this.toastr.success(
                'Ítem eliminado',
                'El ítem se eliminó correctamente de la OA.'
              );
              this.statusChangedResult.emit(this.assemblyOrder);
            },
            error: (err) => {
              this.toastr.error(
                'Error',
                err?.error?.error ?? err?.error ?? 'No se pudo eliminar el ítem.'
              );
            },
          });
        }
      });
  }
}
